/**
 * The schedule's small glyphs, lifted from the boards' own paths
 * (`design/mockups/48-new-request/html/nr-07`, `nr-08`).
 *
 * They are drawn at 10–15 px inside chips, badges and drive labels, where the
 * product's Lucide set at stroke 1.7 is a different weight from the drawing;
 * these keep the boards' stroke and paint in `currentColor`, so the colour is
 * always the token on the element around them.
 */
import type { ReactNode } from 'react'

function Glyph({ size, stroke = 2, children }: { size: number; stroke?: number; children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className="shrink-0"
    >
      {children}
    </svg>
  )
}

/** The start and the end of a day — an office or a home. */
export const HouseGlyph = ({ size = 10 }: { size?: number }) => (
  <Glyph size={size} stroke={2.2}><path d="M3 11l9-8 9 8M5 10v10h14V10" /></Glyph>
)

/** A drive. */
export const TruckGlyph = ({ size = 11 }: { size?: number }) => (
  <Glyph size={size}>
    <path d="M3 6h11v10H3zM14 10h4l3 3v3h-7" /><circle cx="7" cy="18" r="1.8" /><circle cx="17" cy="18" r="1.8" />
  </Glyph>
)

/** A place — the distance badge on a stop. */
export const PinGlyph = ({ size = 10, stroke = 2.2 }: { size?: number; stroke?: number }) => (
  <Glyph size={size} stroke={stroke}>
    <path d="M12 21s7-6.2 7-11a7 7 0 1 0-14 0c0 4.8 7 11 7 11z" /><circle cx="12" cy="10" r="2.5" />
  </Glyph>
)

/** The green block's handle. */
export const GripGlyph = ({ size = 13 }: { size?: number }) => (
  <Glyph size={size}>
    <circle cx="9" cy="6" r="1.2" /><circle cx="15" cy="6" r="1.2" /><circle cx="9" cy="12" r="1.2" />
    <circle cx="15" cy="12" r="1.2" /><circle cx="9" cy="18" r="1.2" /><circle cx="15" cy="18" r="1.2" />
  </Glyph>
)

export const PlusGlyph = ({ size = 12 }: { size?: number }) => (
  <Glyph size={size} stroke={2.2}><path d="M12 5v14M5 12h14" /></Glyph>
)

export const CrossGlyph = ({ size = 11 }: { size?: number }) => (
  <Glyph size={size} stroke={2.4}><path d="M6 6l12 12M18 6L6 18" /></Glyph>
)

export const ChevronRightGlyph = ({ size = 13 }: { size?: number }) => (
  <Glyph size={size}><path d="M9 6l6 6-6 6" /></Glyph>
)

export const ChevronLeftGlyph = ({ size = 15 }: { size?: number }) => (
  <Glyph size={size}><path d="M15 6l-6 6 6 6" /></Glyph>
)

/** Side by side — three columns. */
export const ColumnsGlyph = ({ size = 18, stroke = 1.9 }: { size?: number; stroke?: number }) => (
  <Glyph size={size} stroke={stroke}><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M9 4v16M15 4v16" /></Glyph>
)

/** *Move to …* — two arrows. */
export const MoveGlyph = ({ size = 14.5 }: { size?: number }) => (
  <Glyph size={size}><path d="M7 7h12l-3-3M17 17H5l3 3" /></Glyph>
)

export const WarnGlyph = ({ size = 15 }: { size?: number }) => (
  <Glyph size={size}><path d="M12 4l9 16H3z" /><path d="M12 10v4M12 17v.01" /></Glyph>
)

export const InfoGlyph = ({ size = 15 }: { size?: number }) => (
  <Glyph size={size}><circle cx="12" cy="12" r="9" /><path d="M12 11v6M12 7.5v.01" /></Glyph>
)

export const ClockGlyph = ({ size = 15 }: { size?: number }) => (
  <Glyph size={size}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></Glyph>
)

export const TickGlyph = ({ size = 12 }: { size?: number }) => (
  <Glyph size={size}><path d="M5 12.5l4.5 4.5L19 7.5" /></Glyph>
)

/** The search field's magnifier (Board 6 C). */
export const SearchGlyph = ({ size = 18 }: { size?: number }) => (
  <Glyph size={size}><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></Glyph>
)
